import { createContext, useContext, useState, useCallback } from "react";

const Ctx = createContext(null);

export function useAudioPlayer() {
  return useContext(Ctx) || {};
}

// Global audio state — lives above the router so playback persists across
// page navigation. Supports a single track OR a continuous queue (live channel).
export function AudioPlayerProvider({ children }) {
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [queue, setQueue] = useState([]);       // list of tracks (live channel)
  const [qIndex, setQIndex] = useState(0);
  const [channel, setChannel] = useState(null); // {title, lang, color} when live

  const play = useCallback((t) => {
    if (!t || !t.url) return;
    setQueue([]); setChannel(null); setQIndex(0);
    setTrack((cur) => (cur && cur.id === t.id ? cur : t));
    setPlaying(true);
  }, []);

  // Start a live channel: a looping queue of tracks.
  const playChannel = useCallback((ch) => {
    const tracks = (ch?.tracks || []).filter((t) => t && t.url);
    if (!tracks.length) return;
    setQueue(tracks); setQIndex(0); setChannel({ title: ch.title, title_hi: ch.title_hi, lang: ch.lang, color: ch.color });
    setTrack(tracks[0]); setPlaying(true);
  }, []);

  // Advance within a queue (loops to start — live radio never stops).
  const next = useCallback(() => {
    setQueue((q) => {
      if (!q.length) return q;
      setQIndex((i) => {
        const ni = (i + 1) % q.length;
        setTrack(q[ni]); setPlaying(true);
        return ni;
      });
      return q;
    });
  }, []);

  const hasQueue = queue.length > 0;
  const toggle = useCallback(() => setPlaying((p) => !p), []);
  const close = useCallback(() => { setPlaying(false); setTrack(null); setQueue([]); setChannel(null); setQIndex(0); }, []);

  return (
    <Ctx.Provider value={{ track, playing, setPlaying, play, playChannel, next, hasQueue, channel, queue, qIndex, toggle, close }}>
      {children}
    </Ctx.Provider>
  );
}
